import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Chargement, Message, Vide } from "@cabinet/charte";
import { ecrituresApi, ErreurApi } from "../api/client";
import { formaterNombre } from "../domaine/formats";
import { FicheDePiece } from "./Piece";
import { Pagination, SoldeSigne, TableDuGrandLivre } from "./TableDuGrandLivre";
import { Tiroir } from "./Tiroir";

/** Ce qu'une ligne de balance désigne : un compte ou un préfixe, et un tiers en balance auxiliaire. */
export interface LigneDesignee {
  compte: string;
  libelle: string;
  tiers: string | null;
  solde: number;
}

const TAILLE_DE_PAGE = 200;

/**
 * Remontée d'un solde de balance jusqu'à la pièce (EF-0409).
 *
 * Trois interactions au plus, sans quitter la balance : la ligne s'ouvre sur
 * les mouvements du compte, un mouvement s'ouvre sur sa pièce. `Échap` remonte
 * d'un niveau, puis referme ; la balance reste derrière, telle qu'on l'avait
 * laissée.
 */
export function RemonteeDuSolde({
  dossier,
  exercice,
  ligne,
  jusquAu,
  brouillard,
  surFermeture,
}: {
  dossier: string;
  exercice: string;
  ligne: LigneDesignee;
  jusquAu?: string;
  brouillard: boolean;
  surFermeture: () => void;
}) {
  const [page, setPage] = useState(0);
  const [piece, setPiece] = useState<string | null>(null);

  const livre = useQuery({
    queryKey: ["grand-livre", exercice, ligne.compte, ligne.tiers ?? "", brouillard, page, jusquAu ?? "", "remontee"],
    queryFn: () =>
      ecrituresApi.grandLivre(exercice, {
        compte: ligne.compte,
        tiers: ligne.tiers ?? undefined,
        au: jusquAu,
        brouillard,
        page,
        taille: TAILLE_DE_PAGE,
      }),
    placeholderData: keepPreviousData,
  });

  const intitule = `${ligne.compte} — ${ligne.libelle}${ligne.tiers != null ? ` · ${ligne.tiers}` : ""}`;

  if (piece != null) {
    return (
      <Tiroir
        titre="Pièce"
        surTitre={intitule}
        surFermeture={surFermeture}
        retour={{ libelle: "Mouvements du compte", action: () => setPiece(null) }}
      >
        <FicheDePiece piece={piece} dossier={dossier} compteEnEvidence={ligne.compte} />
      </Tiroir>
    );
  }

  return (
    <Tiroir titre={intitule} surTitre="Mouvements du compte" surFermeture={surFermeture}>
      <p className="discret-texte">
        Solde en balance : <SoldeSigne valeur={ligne.solde} />
        {livre.data != null && <> · {formaterNombre(livre.data.nombreDeMouvements)} mouvements</>}
        {jusquAu ? ` · arrêté au ${new Date(jusquAu).toLocaleDateString("fr-FR")}` : ""}
      </p>

      {livre.error != null && (
        <Message type="erreur" titre={livre.error instanceof ErreurApi ? livre.error.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{livre.error.message}</p>
        </Message>
      )}
      {livre.isPending && <Chargement quoi="les mouvements" />}
      {livre.data != null && livre.data.comptes.length === 0 && <Vide>Aucun mouvement.</Vide>}
      {livre.data != null && (
        <>
          <TableDuGrandLivre livre={livre.data} surMouvement={(mouvement) => setPiece(mouvement.piece)} />
          <Pagination page={livre.data.page} nombreDePages={livre.data.nombreDePages} surPage={setPage} />
        </>
      )}
    </Tiroir>
  );
}
